import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AdminNotesPanel = ({ approval, notes = [], onAddNote, onDeleteNote }) => {
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddNote = () => {
    if (newNote?.trim()) {
      onAddNote({
        id: Date.now(),
        approvalId: approval?.id,
        content: newNote?.trim(),
        createdAt: new Date()?.toISOString(),
        createdBy: 'Admin User'
      });
      setNewNote('');
      setIsAdding(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          <h4 className="text-lg font-semibold text-foreground">Admin Notes</h4>
          {notes?.length > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
              {notes?.length}
            </span>
          )}
        </div>
        {!isAdding && (
          <Button
            size="sm"
            variant="outline"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsAdding(true)}
          >
            Add Note
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="mb-4 p-4 bg-muted/50 rounded-xl border border-border">
          <textarea
            className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none mb-3"
            rows={4}
            placeholder="Add notes about conversations, demo details, follow-up actions..."
            value={newNote}
            onChange={(e) => setNewNote(e?.target?.value)}
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddNote} disabled={!newNote?.trim()}>
              Save Note
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setNewNote('');
                setIsAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {notes?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No notes yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add notes to track conversations and demo details
            </p>
          </div>
        ) : (
          notes?.map((note) => (
            <div
              key={note?.id}
              className="p-4 bg-background rounded-xl border border-border hover:border-primary/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{note?.createdBy}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{formatDate(note?.createdAt)}</span>
                  <button
                    onClick={() => onDeleteNote(note?.id)}
                    className="p-1 hover:bg-error/10 rounded transition-colors"
                    title="Delete note"
                  >
                    <Icon name="Trash2" size={14} className="text-error" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap">{note?.content}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminNotesPanel;